// ./src/components/features/VolunteerSlots/VolunteerRewardEditor/VolunteerRewardEditor.tsx
//
// Repeatable "which voucher, how many" rows for a slot's crew reward. Loads the
// account's reusable vouchers (coupon_type) once. Mirrors the CouponGrantRulesEditor
// shape (voucher Select + qty NumberInput + remove), minus the grant-per control.

import React, { useEffect, useMemo, useState } from "react";
import { Stack, Group, Grid, NumberInput, ActionIcon, Button, Text, Input, Loader } from "@mantine/core";
import { STATUSES } from "@sdflc/utils";

import { CouponType, CouponTypeService, VolunteerSlotRewardInput, listAllPages } from "@/utils/api";
import { AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

export interface VolunteerRewardEditorProps {
  value: VolunteerSlotRewardInput[];
  onChange: (next: VolunteerSlotRewardInput[]) => void;
  disabled?: boolean;
  /**
   * ⚠ The server's refusal for the reward list ("That voucher is not in the library", "Reward quantity must be at least 1", …).
   * Without somewhere to put it, `rewards` sat in `KNOWN_FORM_FIELDS` with no control to render it — and
   * `getFormErrors` puts a KNOWN name in `fields` and NEVER in `genericErrors`, so the message vanished
   * completely and the save failed forever with nothing on screen (found 2026-09-19).
   */
  error?: string | null;
  /**
   * The event this editor is shown on — passed as an authorization context so a cross-account crew
   * member (co_host with tickets.manage) sees the event account's vouchers instead of an empty list.
   */
  eventId?: string | null;
}

const toQty = (v: number | string): number => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
};

export const VolunteerRewardEditor: React.FC<VolunteerRewardEditorProps> = ({ value, onChange, disabled, eventId, error }) => {
  const [vouchers, setVouchers] = useState<CouponType[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    listAllPages(CouponTypeService.list, { reusableOnly: true, status: [STATUSES.ACTIVE], contextEventId: eventId ?? null }, [
      { name: "name", order: "ASC" },
    ]).then((result) => {
      if (cancelled) {
        return;
      }
      if (result.didSucceed()) {
        setVouchers((result.data as CouponType[]) || []);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const voucherData = useMemo(
    () => vouchers.filter((v) => !!v.id).map((v) => ({ value: v.id as string, label: v.name || "Untitled" })),
    [vouchers]
  );

  const setRow = (index: number, patch: Partial<VolunteerSlotRewardInput>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...value, { couponTypeId: null, quantity: 1 }]);
  };

  return (
    <Input.Wrapper
      label="Reward"
      description="Vouchers awarded to each crew member who fills this slot (optional)."
      error={error || undefined}
    >
      <Stack gap="xs" mt={4}>
        {value.length > 0 && (
          <Grid gutter="xs" align="center">
            <Grid.Col span={7}>
              <Text size="sm" c="dimmed">
                Voucher
              </Text>
            </Grid.Col>
            <Grid.Col span={3}>
              <Text size="sm" c="dimmed">
                Quantity
              </Text>
            </Grid.Col>
            <Grid.Col span={2} />
          </Grid>
        )}

        {value.map((row, index) => (
          <Grid key={index} gutter="xs" align="center">
            <Grid.Col span={7}>
              <SelectField
                placeholder={loading ? "Loading…" : "Pick a voucher"}
                data={voucherData}
                value={row.couponTypeId ?? null}
                onChange={(_n, id) => setRow(index, { couponTypeId: id })}
                searchable
                disabled={disabled}
                nothingFoundMessage="No vouchers yet"
                rightSection={loading ? <Loader size="sm" /> : undefined}
                comboboxProps={DRAWER_COMBOBOX_PROPS}
                size="md"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <NumberInput
                value={row.quantity ?? 1}
                onChange={(v) => setRow(index, { quantity: toQty(v) })}
                min={1}
                max={999}
                allowDecimal={false}
                allowNegative={false}
                disabled={disabled}
                size="md"
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Group justify="center">
                <ActionIcon variant="subtle" color="red" onClick={() => removeRow(index)} disabled={disabled} aria-label="Remove reward">
                  <AppIcon iconName="Trash" size={16} />
                </ActionIcon>
              </Group>
            </Grid.Col>
          </Grid>
        ))}

        <Group>
          <Button
            variant="light"
            size="md"
            leftSection={<AppIcon iconName="Plus" size={16} />}
            onClick={addRow}
            disabled={disabled}
          >
            Add reward
          </Button>
        </Group>
      </Stack>
    </Input.Wrapper>
  );
};

export default VolunteerRewardEditor;
